/*
 * Pass in the array of modules (moduleAds) or thumbnails (seeAllSlidesAds),
 * get ad placements (but not the ad POSITION strings... those are handled
 * by `getAdPosition.js`).
 *
 * Usage (moduleAds is the example here, but seeAllSlides ads works similarly):
 *
 * const modules = [
 *   {
 *      type: 'hero', rows: [ {...}, {...}, {...} ]
 *   }
 * ];
 * const result = moduleAds(modules);
 *
 * // Result looks like:
 * [
 *   {
 *     rows: [
 *       {
 *         hasDesktopAd: false,
 *         hasTabletAd: false,
 *         hasMobileAd: false,
 *         cards: [
 *           { hasMobileAd: false },
 *           { hasMobileAd: false },
 *           { hasMobileAd: false }
 *         ]
 *       },
 *       {
 *         hasDesktopAd: false,
 *         hasTabletAd: false,
 *         hasMobileAd: false,
 *         cards: [
 *           { hasMobileAd: false },
 *           { hasMobileAd: false },
 *           { hasMobileAd: false }
 *         ]
 *       },
 *       {
 *         hasDesktopAd: true,
 *         hasTabletAd: true,
 *         hasMobileAd: true,
 *         cards: [
 *           { hasMobileAd: false },
 *           { hasMobileAd: false },
 *           { hasMobileAd: true }
 *         ]
 *       },
 *     ]
 *   }
 * ]
 *
 *
 * Basic logic: for most module types, put a desktop/tablet ad in the last row.
 * Put a mobile ad in the last _card_. (On mobile, individual cards are
 * rendered as entire rows.)
 *
 * For related content or feed modules, use the ad cadence configured
 * below.
 *
 * We could extract this into a separate repo just like `@r29/section-ads`
 * in case we'd like to show ad placeholders in DCE!
 */

const MODULE_AD_CADENCES = {
  'related-content': {
    desktopCadence: { interval: 3, startAt: 0 },
    tabletCadence: { interval: 2, startAt: 0 },
    mobileCadence: { interval: 3, startAt: 2 }
  },
  'editors-picks': {
    // Never show ads in the 'editors-picks' module on any device
    desktopCadence: { interval: 0, startAt: 0 },
    tabletCadence: { interval: 0, startAt: 0 },
    mobileCadence: { interval: 0, startAt: 0 }
  },
  feed: {
    desktopCadence: { interval: 4, startAt: 1 },
    tabletCadence: { interval: 4, startAt: 1 },
    mobileCadence: { interval: 4, startAt: 1 }
  },
  hp_refresh_feed_ads: {
    desktopCadence: { interval: 4, startAt: 3 },
    tabletCadence: { interval: 4, startAt: 3 },
    mobileCadence: { interval: 4, startAt: 3, cardMax: 4 }
  }
};

const SEE_ALL_SLIDES_CADENCE = { interval: 4, startAt: 1, max: 15 };

export const moduleAdsCadence = (modules) =>
  modules.map(module => (
    Object.keys(MODULE_AD_CADENCES).includes(module.type)
      ? moduleWithIntervalAds(module)
      : moduleWithSingleBottomAd(module)
  ));

const getAdCadence = (items, customCadence) => {
  const { interval, startAt, max } = customCadence;
  return items.map((item, index) => {
    const adMod = (index - startAt) % interval;
    const adIndex = (index - startAt) / interval;
    return {
      hasAd: adMod === 0 && adIndex < max && adIndex >= 0
    };
  });
};

export const seeAllSlidesAds = (thumbnails) =>
  getAdCadence(thumbnails, SEE_ALL_SLIDES_CADENCE);

function moduleWithIntervalAds(module) {
  const {
    desktopCadence, tabletCadence, mobileCadence
  } = MODULE_AD_CADENCES[module.type];
  let cardIndexInModule = 0;

  if (!module.rows) {
    return {};
  }

  return {
    rows: module.rows.map((row, rowIndex) => {
      const desktopMod = (rowIndex - desktopCadence.startAt)
        % desktopCadence.interval;
      const tabletMod = (rowIndex - tabletCadence.startAt)
        % tabletCadence.interval;
      const mobileMod = (rowIndex - mobileCadence.startAt)
        % mobileCadence.interval;
      return {
        hasDesktopAd: desktopMod === 0,
        hasTabletAd: tabletMod === 0,
        hasMobileAd: mobileMod === 0,
        cards: row.cards.map(() => {
          const mobileModForCards = (cardIndexInModule - mobileCadence.startAt)
            % mobileCadence.interval;
          const mobileAdIndexForCards = ((cardIndexInModule - mobileCadence.startAt)
            / mobileCadence.interval);
          cardIndexInModule++;
          return {
            hasMobileAd: mobileCadence.cardMax
              ? mobileModForCards === 0 && mobileAdIndexForCards < mobileCadence.cardMax
              : mobileModForCards === 0
          };
        })
      };
    })
  };
}

function moduleWithSingleBottomAd(module) {
  if (!module.rows) {
    return {};
  }

  return {
    rows: module.rows.map((row, rowIndex) => {
      const isHTML = module.type === 'html';
      const isLastRow = rowIndex === module.rows.length - 1;
      const hasAd = !isHTML && isLastRow;
      return {
        hasDesktopAd: hasAd,
        hasTabletAd: hasAd,
        hasMobileAd: hasAd,
        cards: row.cards.map((entity, entityIndex) => {
          const isLastCard = entityIndex === row.cards.length - 1;
          return {
            hasMobileAd: hasAd && isLastCard
          };
        })
      };
    })
  };
}

